import { message } from 'antd'

import LocalStorageWrapper from '~/lib/LocalStorageWrapper'
import { DFM_FAILURE, PROCESSID_CNC } from '~/lib/partsConstants'
import logger from '~/lib/logger'

import {
    lastUpdatedQuery,
    updateModelConfigurationDfmInspectionMutation,
    addModelConfigurationDfmInspectionMutation,
    updateDfmFeedbackInfoMutation,
    publishQuoteModelRevisionMutation,
} from './query'
import { canUpdateInspectionData, canReleaseInspection } from './logicV2'
import { RACE_CONDITION_ERR_MSG, PASS_AND_RELEASE_ERROR } from './constants'

const storage = new LocalStorageWrapper()
const REPROCESSING_TIMER_INTERVAL = 10000

const check = async (props, callback) => {
    const {
        qmr: { id },
        client,
        refetch,
    } = props
    const result = await client.query({
        query: lastUpdatedQuery,
        variables: {
            id,
        },
        fetchPolicy: 'network-only',
    })
    const now = new Date()
    const then = new Date(storage.getItem(`lastUpdatedAt-${id}`) || 0)
    const lastUpdatedAt = storage.getItem(`lastUpdated-${id}`)
    const FIVE_MINUTES = 300000

    if (
        (now - then) / FIVE_MINUTES > 1 || // cancel check if it's been over 5 minutes
        result?.data?.quoteModelRevision?.partRevision?.updatedAt !==
            lastUpdatedAt
    ) {
        window.clearInterval(window.reprocessCheck)
        storage.removeItem(`lastUpdated-${id}`)
        storage.removeItem(`lastUpdatedAt-${id}`)
        callback()
        refetch()
    }
}

export function reprocessingTimer(props, callback) {
    const {
        qmr: {
            partRevision: { updatedAt },
            id,
        },
    } = props

    storage.setItem(`lastUpdated-${id}`, updatedAt)
    if (!storage.getItem(`lastUpdatedAt-${id}`)) {
        storage.setItem(`lastUpdatedAt-${id}`, new Date())
    }

    window.reprocessCheck = window.setInterval(
        check,
        REPROCESSING_TIMER_INTERVAL,
        props,
        callback,
    )

    return updatedAt
}
export function getEmailForPartDFMReport(isAdminQuoting, source, email) {
    if (isAdminQuoting || source === 'Customer') {
        return `${email},`
    }
}

export function isReprocessing(props) {
    if (!props.qmr) {
        return false
    }

    const {
        qmr: {
            partRevision: { updatedAt },
            id,
        },
    } = props
    const lastUpdated = storage.getItem(`lastUpdated-${id}`)

    return lastUpdated === updatedAt
}

export function hasFailingFeedback(feedback) {
    return feedback.some(f => f?.severity === DFM_FAILURE)
}

export const saveAndRelease = async ({
    client,
    release,
    inspectionData,
    quoteModelRevision,
    isImQuoteType,
    override,
    quantityRangeData,
    minMarkupFlagPct,
    is3dp,
}) => {
    const hasFailingDfmFeedback = hasFailingFeedback(
        quoteModelRevision.dfmFeedback,
    )

    // If this returns false, we won't even release dfm feedback
    const canUpdateDfmInspection = canUpdateInspectionData(
        inspectionData,
        quoteModelRevision,
        hasFailingDfmFeedback,
        minMarkupFlagPct,
    )

    if (!canUpdateDfmInspection) {
        return
    }
    const { process } = quoteModelRevision?.modelConfiguration || {}
    const dfmInspection =
        quoteModelRevision.modelConfiguration?.dfmInspections?.find(item => {
            return (
                item.quantityLowerRange ===
                    quantityRangeData.quantityLowerRange &&
                item.quantityUpperRange === quantityRangeData.quantityUpperRange
            )
        })

    try {
        if (process?.id !== PROCESSID_CNC) {
            await client.mutate({
                mutation: updateModelConfigurationDfmInspectionMutation,
                variables: {
                    modelConfigurationDfmInspectionId:
                        quoteModelRevision.modelConfiguration?.dfmInspection
                            ?.id,
                    inspectionData,
                },
            })
        } else if (dfmInspection?.id && process?.id === PROCESSID_CNC) {
            await client.mutate({
                mutation: updateModelConfigurationDfmInspectionMutation,
                variables: {
                    modelConfigurationDfmInspectionId: dfmInspection?.id,
                    inspectionData,
                    lastModifiedAt: dfmInspection?.updatedAt,
                    override: override,
                    isQuantityRangeAvailable:
                        quantityRangeData.isQuantityRangeAvailable,
                },
            })
        } else if (process?.id === PROCESSID_CNC) {
            await client.mutate({
                mutation: addModelConfigurationDfmInspectionMutation,
                variables: {
                    modelConfigurationId:
                        quoteModelRevision.modelConfiguration?.id,
                    inspectionData,
                    quantityRangeData,
                },
            })
        }
    } catch (err) {
        if (err.message === RACE_CONDITION_ERR_MSG) {
            return RACE_CONDITION_ERR_MSG
        }
        logger.exception(err, {
            extra: {
                info: err,
            },
        })
        message.error('Something went wrong while saving')
    }

    if (release) {
        if (
            !canReleaseInspection(
                inspectionData,
                hasFailingDfmFeedback,
                isImQuoteType,
                is3dp,
            )
        ) {
            quoteModelRevision.dfmFeedback.forEach(async d => {
                if (!d.isPublished) {
                    await client.mutate({
                        mutation: updateDfmFeedbackInfoMutation,
                        variables: {
                            dfmFeedbackId: d.id,
                            isPublished: true,
                        },
                    })
                }
            })

            message.success('DFM feedback released.')
        } else {
            if (process?.id === PROCESSID_CNC && !dfmInspection?.isPublished) {
                let isError = false

                await client
                    .mutate({
                        mutation: publishQuoteModelRevisionMutation,
                        variables: {
                            quoteModelRevisionId: quoteModelRevision.id,
                            quantityLowerRange:
                                quantityRangeData.quantityLowerRange,
                            quantityUpperRange:
                                quantityRangeData.quantityUpperRange,
                        },
                    })
                    .catch(error => {
                        message.error(`Failed to release Inspection. ${error}`)
                        isError = true
                    })

                if (hasFailingDfmFeedback) {
                    message.success('Inspection failed and released.')
                } else {
                    message.success('Inspection passed and released.')
                }
                if (isError) {
                    return PASS_AND_RELEASE_ERROR
                }
            } else if (
                process?.id !== PROCESSID_CNC &&
                !quoteModelRevision?.modelConfiguration?.dfmInspection
                    ?.isPublished
            ) {
                await client
                    .mutate({
                        mutation: publishQuoteModelRevisionMutation,
                        variables: {
                            quoteModelRevisionId: quoteModelRevision.id,
                        },
                    })
                    .catch(error => {
                        message.error(`Failed to release Inspection. ${error}`)
                    })

                if (hasFailingDfmFeedback) {
                    message.success('Inspection failed and released.')
                } else {
                    message.success('Inspection passed and released.')
                }
            } else {
                quoteModelRevision.dfmFeedback.forEach(async d => {
                    if (!d.isPublished) {
                        await client.mutate({
                            mutation: updateDfmFeedbackInfoMutation,
                            variables: {
                                dfmFeedbackId: d.id,
                                isPublished: true,
                            },
                        })
                    }
                })

                message.success(`${process?.name} pricing parameters updated.`)
            }
        }
    } else {
        message.success(`${process?.name} pricing parameters saved.`)
    }
    client.reFetchObservableQueries()
}
